import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import {
  editSettingsMsg,
  instructionsForServiceMsg,
  instructionsMsg,
  seeMsg,
  t,
  timeSettingsMsg,
} from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { editSection } from '../../selfservice/actions';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import { PbxTimeSettingsAccordionContent } from '../PbxTimeSettingsAccordionContent/PbxTimeSettingsAccordionContent';
import type { DialogParams } from '../../common/types/dialog';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store.js';

export interface VoiceSubscriptionTimeSettingsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
}

export const VoiceSubscriptionTimeSettings = ({
  subscription,
  hasPendingActions,
}: VoiceSubscriptionTimeSettingsProps) => {
  const dispatch = useDispatch();
  const numberRanges = useSelector((state: State) => state.resources?.numberRanges || {}, deepEqual);
  const { editingSection, errors, saving } = useSelector((state: State) => ({
    editingSection: state?.selfservice?.pendingSubscriptionActions?.editingSection || '',
    errors: state?.selfservice?.pendingSubscriptionActions?.errors,
    saving: state?.selfservice?.pendingSubscriptionActions?.saving,
  }));
  const isEditable =
    subscription.details!.specialNumber?.pbxConfigurationDetails.pbxType ===
      SubscriptionPbxDetails.PbxTypeEnum.TAVOITETTAVUUSKETJU ||
    subscription.details!.specialNumber?.pbxConfigurationDetails.pbxType === SubscriptionPbxDetails.PbxTypeEnum.VAKIO;
  const numberRange = subscription?.details?.mobile?.pbxConfiguration?.corporateNumberRangeId
    ? numberRanges[subscription.details.mobile.pbxConfiguration.corporateNumberRangeId]
    : undefined;
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const onEditSectionIfNoActionsPending = (section: string) => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return false;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return false;
    }
    dispatch(editSection(section));
    return true;
  };
  const specialNumberOnEdit = (section: string) => {
    if (isEditable) {
      return onEditSectionIfNoActionsPending(section);
    }
    return onShowDialog({
      subscriptionId: subscription.subscriptionId,
      type: DialogType.SUBSCRIPTION_CHANGE_SPECIAL_NUMBER,
    });
  };

  return (
    <DetailsWithCard
      cardContent={
        <>
          <h3>{t.RD4R(instructionsMsg)}</h3>
          <p>
            {t.LJ10(seeMsg)}
            <Link to={paths.INSTRUCTIONS_VAKIO} target="_blank" rel="noopener noreferrer">
              {t.Z72R(instructionsMsg.toLowerCase())}
            </Link>
            {t.U63I(instructionsForServiceMsg)}
          </p>
        </>
      }
    >
      <HeaderWithEditButton
        title={t.SC8P(timeSettingsMsg)}
        showButton={editingSection !== 'pbxTimeSettings'}
        buttonText={t.YBJF(editSettingsMsg)}
        onClick={() => specialNumberOnEdit('pbxTimeSettings')}
      />
      <PbxTimeSettingsAccordionContent
        dispatch={dispatch}
        editing={editingSection === 'pbxTimeSettings'}
        errors={errors}
        subscriptionPbxConfiguration={subscription.details!.specialNumber!}
        numberRange={numberRange}
        onCancel={() => onEditSectionIfNoActionsPending('')}
        pbxSolution={subscription}
        saving={saving ?? false}
        subscriptionId={subscription.subscriptionId}
        subscriptionUserFriendlyId={subscription.subscriptionUserFriendlyId}
      />
    </DetailsWithCard>
  );
};
